import { jsx } from "react/jsx-runtime";
import { Fragment } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Navigate } from "react-router-dom";
import { isDisabledVip } from "@nxplatform/customer-noauth/slices/customer-noauth.slice";
import { CabinetRoutes } from "@nxplatform/helpers/entities";
import { selectResponsibleGamblingTools, selectIsReferralProgramEnabled } from "@nxplatform/platform-settings/brand-settings.slice";
import { allowedLinks } from "@nxplatform/platform-settings/locale-setup.routes";
import { isDisabledPromotions, selectLocaleSetupSlugLanguage } from "@nxplatform/platform-settings/locale-setup.selectors";
import { selectCustomerTokenEntities } from "@nxplatform/store-shared/customer/shared-customer-selectors";
import { mergeRoutes, filterRoutes } from "./helpers.js";
import { RedirectWithQueryParams } from "./redirect-with-query-params.js";
import { selectIntegrations, selectLimitsSelfDeletionEnabled, selectLimitsSelfExclusionEnabled, selectPlatformSettingLimitsEnabled, selectPlatformSettingsCasinoEnabled, selectPlatformSettingsDisableSportsBetting } from "@nxplatform/platform-settings/platform-settings.selectors";
const limitsWithSection = [
  CabinetRoutes.responsibleGamblingLimits,
  CabinetRoutes.responsibleGamblingExclusion,
  CabinetRoutes.responsibleGamblingDeactivation,
  CabinetRoutes.responsibleGamblingCoolingOff
];
const routesLimits = [
  CabinetRoutes.editLimit,
  CabinetRoutes.addLimit,
  CabinetRoutes.newLimits
];
const useCreateCabinetRoutes = (pages, Container, additional) => {
  const {
    auth,
    languageSlug,
    disabledPromotions,
    disabledVip,
    enabledCasino,
    limitsEnabled,
    responsibleGamblingTools,
    selfExclusionEnabled,
    selfDeletionEnabled,
    referralProgramEnabled,
    integrations,
    disableSportsBetting
  } = useSelector((state) => ({
    auth: selectCustomerTokenEntities(state),
    languageSlug: selectLocaleSetupSlugLanguage(state),
    referralProgramEnabled: selectIsReferralProgramEnabled(state),
    disabledPromotions: isDisabledPromotions(state),
    disableSportsBetting: selectPlatformSettingsDisableSportsBetting(state),
    disabledVip: isDisabledVip(state),
    enabledCasino: selectPlatformSettingsCasinoEnabled(state),
    limitsEnabled: selectPlatformSettingLimitsEnabled(state),
    responsibleGamblingTools: selectResponsibleGamblingTools(state),
    selfExclusionEnabled: selectLimitsSelfExclusionEnabled(state),
    selfDeletionEnabled: selectLimitsSelfDeletionEnabled(state),
    integrations: selectIntegrations(state)
  }), shallowEqual);
  if (!pages) return [];
  const {
    CustomerDeposit,
    PersonalDetailsContainer,
    PersonalPreferences,
    AccountVerification,
    TransactionHistory,
    ForecastHistory,
    CustomerWithdrawal,
    CustomerPromotions,
    CustomerCoins,
    CabinetCasinoHistory,
    PageLimits,
    EditLimits,
    ResponsibleGamblingLimits,
    ResponsibleGamblingExclusion,
    ResponsibleGamblingDeactivation,
    CoolingOff,
    ReportsAndMessages,
    CabinetBettingHistory,
    ContentSection = Fragment,
    CabinetCasinoSessionHistory,
    FinancialReport,
    AccountVerificationInfo,
    SectionHeader = Fragment,
    CabinetReferrals,
    CabinetSecurity,
    CabinetStatistics,
    AccountOverview,
    PromotionsHistory
  } = pages;
  const {
    limits,
    selfExclusion,
    accountDeactivation,
    coolingOff
  } = responsibleGamblingTools;
  const { neccton } = integrations || {};
  const { active } = accountDeactivation || {};
  const newLimitsAllowed = limits;
  const exclusionAllowed = selfExclusion && selfExclusionEnabled;
  const coolingOffAllowed = coolingOff;
  const deactivationAllowed = active && selfDeletionEnabled;
  const LinkComponents = {
    [CabinetRoutes.deposit]: CustomerDeposit,
    [CabinetRoutes.details]: PersonalDetailsContainer,
    [CabinetRoutes.preferences]: PersonalPreferences,
    [CabinetRoutes.verification]: AccountVerification,
    [CabinetRoutes.verificationInfo]: AccountVerificationInfo,
    [CabinetRoutes.transaction]: TransactionHistory,
    [CabinetRoutes.forecast]: ForecastHistory,
    [CabinetRoutes.withdrawals]: CustomerWithdrawal,
    [CabinetRoutes.promotions]: CustomerPromotions,
    [CabinetRoutes.coins]: CustomerCoins,
    [CabinetRoutes.casino]: CabinetCasinoHistory,
    [CabinetRoutes.jackpot]: null,
    [CabinetRoutes.newLimits]: PageLimits,
    [CabinetRoutes.addLimit]: EditLimits,
    [CabinetRoutes.editLimit]: EditLimits,
    [CabinetRoutes.responsibleGamblingLimits]: ResponsibleGamblingLimits,
    [CabinetRoutes.responsibleGamblingExclusion]: ResponsibleGamblingExclusion,
    [CabinetRoutes.responsibleGamblingDeactivation]: ResponsibleGamblingDeactivation,
    [CabinetRoutes.responsibleGamblingCoolingOff]: CoolingOff,
    [CabinetRoutes.reportsAndMessages]: ReportsAndMessages,
    [CabinetRoutes.history]: CabinetBettingHistory,
    [CabinetRoutes.casinoSession]: CabinetCasinoSessionHistory,
    [CabinetRoutes.reports]: FinancialReport,
    [CabinetRoutes.referrals]: CabinetReferrals,
    [CabinetRoutes.security]: CabinetSecurity,
    [CabinetRoutes.statistics]: CabinetStatistics,
    [CabinetRoutes.overview]: AccountOverview,
    [CabinetRoutes.promotionsHistory]: PromotionsHistory
  };
  const cabinetMenuList = Object.keys(LinkComponents).filter((menu) => !enabledCasino ? menu !== CabinetRoutes.casino : true).filter((menu) => !limitsEnabled ? !routesLimits.includes(menu) : true).filter((menu) => disabledPromotions ? menu !== CabinetRoutes.promotions && menu !== CabinetRoutes.forecast : true).filter((menu) => disableSportsBetting ? menu !== CabinetRoutes.history && menu !== CabinetRoutes.forecast : true).filter((menu) => disabledVip ? menu !== CabinetRoutes.coins : true).filter((menu) => !newLimitsAllowed ? menu !== CabinetRoutes.responsibleGamblingLimits : true).filter((menu) => !exclusionAllowed ? menu !== CabinetRoutes.responsibleGamblingExclusion : true).filter((menu) => !deactivationAllowed ? menu !== CabinetRoutes.responsibleGamblingDeactivation : true).filter((menu) => !coolingOffAllowed ? menu !== CabinetRoutes.responsibleGamblingCoolingOff : true).filter((menu) => !referralProgramEnabled ? menu !== CabinetRoutes.referrals : true).filter((menu) => !neccton ? menu !== CabinetRoutes.reportsAndMessages : true);
  const routes = [
    {
      path: allowedLinks.cabinet,
      element: auth ? /* @__PURE__ */ jsx(Container, {}) : /* @__PURE__ */ jsx(RedirectWithQueryParams, { languageSlug }),
      children: auth ? mergeRoutes([
        ...cabinetMenuList.map((link) => {
          const Component = LinkComponents[link];
          if (!Component) return null;
          return {
            path: `${allowedLinks.cabinet}${link}`,
            element: limitsWithSection.includes(link) ? /* @__PURE__ */ jsx(Component, { ContentSection, SectionHeader }) : /* @__PURE__ */ jsx(Component, {})
          };
        }).filter(filterRoutes),
        { path: "*", element: /* @__PURE__ */ jsx(Navigate, { to: allowedLinks.home, replace: true }) }
      ], additional) : [{ path: "*", element: /* @__PURE__ */ jsx(RedirectWithQueryParams, { languageSlug }) }]
    }
  ];
  return routes;
};
export {
  useCreateCabinetRoutes
};
